/**
 * Field Structure Context for ticket creation workflow
 * Adapted from Thaklis app FieldStructureContext
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  createFieldStructure,
  processFieldsIntoStructure,
  updateStructureForContext,
  isDocumentField
} from './utils';
import { CONTEXT_TYPES, STATIC_DOCUMENT_FIELDS, STATIC_FIELD_CATEGORY_MAP } from './constants';

export const FieldStructureContext = createContext();

export const FieldStructureProvider = ({ children }) => {
  const [fieldStructure, setFieldStructure] = useState(null);
  const [currentContext, setCurrentContext] = useState(CONTEXT_TYPES.DEFAULT);
  const [documentFields, setDocumentFields] = useState(new Set());

  const initializeStructure = useCallback((serviceIdOrStructure) => {
    if (typeof serviceIdOrStructure === 'string') {
      const newStructure = createFieldStructure(serviceIdOrStructure);
      setFieldStructure(newStructure);
      return newStructure;
    }
    setFieldStructure(serviceIdOrStructure);
    return serviceIdOrStructure;
  }, []);

  const processServiceResponse = useCallback((response, serviceId) => {
    const structure = createFieldStructure(serviceId);
    let updated = structure;
    if (response.staticFields && Array.isArray(response.staticFields)) {
      updated = processFieldsIntoStructure(structure, response.staticFields);
    }
    setFieldStructure(updated);
    if (response.staticFields && Array.isArray(response.staticFields)) {
      const docFields = response.staticFields.filter(f => isDocumentField(f));
      setDocumentFields(new Set(docFields));
    }
    return updated;
  }, []);

  const updateContext = useCallback((context) => {
    setCurrentContext(context);
  }, []);

  const registerDocumentField = useCallback((fieldName) => {
    setDocumentFields(prev => new Set([...prev, fieldName]));
  }, []);

  const contextualStructure = useMemo(() => {
    if (!fieldStructure) return null;
    return updateStructureForContext(fieldStructure, currentContext);
  }, [fieldStructure, currentContext]);

  const shouldShowFieldInSection = useCallback((fieldName, section) => {
    if (!fieldName) return false;
    const isDocument = isDocumentField(fieldName);

    if (currentContext === CONTEXT_TYPES.DEFAULT) {
      if (section === 'CUSTOMER_INFO') return true;
      if (section === 'DOCUMENTS') return false;
    }

    if (currentContext === CONTEXT_TYPES.EMPLOYEE_SELECTED) {
      if (STATIC_DOCUMENT_FIELDS.includes(fieldName)) {
        return section === 'CUSTOMER_INFO';
      }
      const fieldCategory = STATIC_FIELD_CATEGORY_MAP[fieldName];
      if (section === 'CUSTOMER_INFO') {
        return fieldCategory === 'PERSONAL_INFO' || !isDocument;
      }
      if (section === 'DOCUMENTS') {
        return (fieldCategory === 'DOCUMENTS' || isDocument) && !STATIC_DOCUMENT_FIELDS.includes(fieldName);
      }
    }

    return true;
  }, [currentContext]);

  const getFieldsForSection = useCallback((section) => {
    if (!contextualStructure) return [];
    const allFields = Object.keys(contextualStructure.fieldProperties || {});
    return allFields.filter(fn => shouldShowFieldInSection(fn, section));
  }, [contextualStructure, shouldShowFieldInSection]);

  const value = {
    fieldStructure,
    contextualStructure,
    currentContext,
    documentFields,
    initializeStructure,
    processServiceResponse,
    updateContext,
    registerDocumentField,
    shouldShowFieldInSection,
    getFieldsForSection,
    isDocumentField
  };

  return (
    <FieldStructureContext.Provider value={value}>
      {children}
    </FieldStructureContext.Provider>
  );
};

export const useFieldStructure = () => {
  const context = useContext(FieldStructureContext);
  if (!context) {
    throw new Error('useFieldStructure must be used within a FieldStructureProvider');
  }
  return context;
};

export default FieldStructureContext;
